import React, {useMemo, useState} from 'react';

import {TimestampAwareMarkdown} from '@/components/timestamp_aware_markdown';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(utc);

import {useSelectorWithRefresh} from '@/store/kysely_module';
import {NestedComment} from '@/store/selectors/selectors';

import {CommentForm} from './comment_form';
import {ActionIcon, Group, Menu, Paper, Text, TypographyStylesProvider} from '@mantine/core';
import {useModals} from '../modals/modals_provider';
import {useIsMobile} from '@/utils/user_agent';
import {IconArrowForward, IconDotsVertical} from '@tabler/icons-react';

import './comments.css';
import {useCurrentUser} from '@/modules/music_sniper/hooks/useCurrentUser';
import {UserPopover} from './user_popover';
import {useAvatarImage} from '@/hooks/useAvatarImage';
import {Avatar} from '@/components/Avatar';

type WorkspaceUser = {
    id: string;
    name?: string | null;
    email?: string | null;
};

type CommentProps = {
    comment: NestedComment;
    clickedTimestamp: (timestamp: string) => void;
    deleteComment: (commentId: string) => void;
    editComment: (commentId: string, text: string) => void;
    replyToComment: (text: string, parentId: string) => Promise<void>;
    isReply?: boolean;
    isLastReply?: boolean;
    showUser?: boolean;
    hideAvatar?: boolean;
    onInitialFocus?: () => string;
    hideActions?: boolean;
    workspaceUsers?: WorkspaceUser[];
    rootCommentId?: string;
};

export const Comment = (props: CommentProps) => {
    const currentUser = useCurrentUser();
    const isAuthorCurrentUser = props.comment.created_by === currentUser?.id;
    const [isEditing, setIsEditing] = useState(false);
    const modals = useModals();

    const [author] = useSelectorWithRefresh('getUser', {user_id: props.comment.created_by}, null, {workspace_id: props.comment.workspace_id});

    const {data: avatarUrl} = useAvatarImage(props.comment.created_by, author?.profile_picture_file_id || null, author?.updated_at || null);

    const [isReplying, setIsReplying] = useState(false);

    const handleDelete = async () => {
        modals.openModal('ConfirmationModal', {
            title: 'Delete this comment?',
            message: 'This action cannot be undone.',
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: () => {
                props.deleteComment(props.comment.id);
            },
        });
    };

    const handleReplyClick = () => {
        setIsReplying(!isReplying);
    };

    const timeDisplay = useMemo(() =>
        dayjs.utc(props.comment.created_at, 'YYYY-MM-DD HH:mm:ss').local().fromNow(),
        [props.comment.created_at]);

    const isMobile = useIsMobile();

    const actionMenu = !props.hideActions && (
        <Menu
            closeOnItemClick={true}
        >
            <Menu.Target>
                <ActionIcon
                    variant='hover'
                    style={{
                        position: 'absolute',
                        right: 0,
                        backgroundColor: 'transparent',
                        color: 'var(--mantine-color-text)',
                    }}
                >
                    <IconDotsVertical
                        size='16px'
                        strokeWidth={1}
                    />
                </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Item
                    onClick={() => {
                        setIsEditing(true);
                    }}
                >
                    Edit
                </Menu.Item>
                <Menu.Item
                    onClick={handleDelete}
                >
                    Delete
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );

    const replyIcon = (
        <ActionIcon
            variant='hover'
            style={{
                backgroundColor: 'transparent',
            }}
            onClick={handleReplyClick}
            aria-label="Reply"
        >
            <IconArrowForward
                style={{color: 'var(--mantine-color-text)'}}
                strokeWidth={0.8}
                size={'1.2rem'}
            />
        </ActionIcon>
    );

    const showReplyArrow = (
        (!props.isReply && !Boolean(props.comment.replies.length)) ||
        (props.isReply && props.isLastReply)
    );

    const commentOrEditor = isEditing ? (
        <CommentForm
            initialValue={props.comment.text}
            isReply={props.isReply}
            onCancel={() => setIsEditing(false)}
            onSubmit={async (text) => {
                props.editComment(props.comment.id, text);
                setIsEditing(false);
            }}
        />
    ) : (
        <CommentMarkdown
            clickedTimestamp={props.clickedTimestamp}
            text={props.comment.text}
            workspaceUsers={props.workspaceUsers}
            workspaceId={props.comment.workspace_id}
        />
    );

    return (
        <Paper
            // withBorder
            radius='md'
            style={{
                paddingLeft: (!isMobile || props.isReply) ? 'var(--mantine-spacing-lg)' : '',
                // padding: !isMobile ? 'var(--mantine-spacing-lg) var(--mantine-spacing-xl)' : '',
                maxWidth: '600px',
                marginBottom: '0',
                position: 'relative',
            }}
        >
            <Group>
                {isAuthorCurrentUser && actionMenu}
                {props.showUser && !props.hideAvatar && author && (
                    <UserPopover userId={author.id} userName={author.name}>
                        <Avatar
                            src={avatarUrl}
                            userName={author.name || 'user'}
                            size='sm'
                            radius="xl"
                        />
                    </UserPopover>
                )}
                <div>
                    {props.showUser && author && (
                        <UserPopover userId={author.id} userName={author.name}>
                            <Text size='xs' style={{cursor: 'pointer'}}>
                                {author.name || 'user'}
                            </Text>
                        </UserPopover>
                    )}
                </div>
            </Group>
            <TypographyStylesProvider
                // className={classes.body}
                style={{
                    paddingLeft: '54px',
                    paddingTop: 'var(--mantine-spacing-sm)',
                    paddingBottom: 'var(--mantine-spacing-lg)',
                    fontSize: 'var(--mantine-font-size-sm)',
                    userSelect: 'text',
                }}
            >
                <Text size="xs" c="dimmed" style={{fontSize: '10px'}}>
                    {timeDisplay}
                </Text>
                {commentOrEditor}
                {showReplyArrow && replyIcon}
            </TypographyStylesProvider>
            {/* <div>
                {actions}
            </div> */}
            {/* </div> */}
            {
                Boolean(props.comment.replies.length) && (
                    <div>
                        {props.comment.replies.map((c, i) => (
                            <Comment
                                {...props}
                                key={c.id}
                                replyToComment={(text, parentId) => props.replyToComment(text, parentId)}
                                // showUser={(i === 0) || (props.comment.replies[i].created_by !== c.created_by)}
                                showUser={true}
                                comment={c}
                                isReply={true}
                                isLastReply={i === props.comment.replies.length - 1}
                                rootCommentId={props.comment.id}
                            />
                        ))}
                    </div>
                )
            }
            {isReplying && (
                <CommentForm
                    onInitialFocus={props.onInitialFocus}
                    isReply={true}
                    onCancel={() => setIsReplying(false)}
                    onSubmit={async (text) => {
                        await props.replyToComment(text, props.rootCommentId || props.comment.id);
                        setIsReplying(false);
                    }} />
            )}
        </Paper>
    );
};

type CommentMarkdownProps = {
    text: string;
    clickedTimestamp: (timestamp: string) => void;
    workspaceUsers?: WorkspaceUser[];
    workspaceId?: string;
};

export const CommentMarkdown = (props: CommentMarkdownProps) => {
    return (
        <TimestampAwareMarkdown
            text={props.text}
            clickedTimestamp={props.clickedTimestamp}
            className='comment-body'
            workspaceUsers={props.workspaceUsers}
            workspaceId={props.workspaceId}
        />
    );
}
